import React, { useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import {
  Send, Eye, Users, Loader2, List,
  Clock, TrendingUp, Calendar, Play, UserPlus, Activity, Save
} from 'lucide-react';
import { useAdminEmail } from './AdminEmailContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const COMMON_TIMEZONES = [
  'Pacific/Auckland',
  'Australia/Sydney',
  'Asia/Tokyo',
  'Asia/Singapore',
  'Asia/Kolkata',
  'Europe/London',
  'Europe/Berlin',
  'America/New_York',
  'America/Los_Angeles',
  'UTC',
];

const HeartbeatScheduleEditor = () => {
  const [hour, setHour] = useState(13);
  const [minute, setMinute] = useState(30);
  const [tz, setTz] = useState('Pacific/Auckland');
  const [nextRun, setNextRun] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/email-scheduler/heartbeat-schedule`, { withCredentials: true });
      const s = res.data.schedule || {};
      setHour(typeof s.hour === 'number' ? s.hour : 13);
      setMinute(typeof s.minute === 'number' ? s.minute : 30);
      setTz(s.timezone || 'Pacific/Auckland');
      setNextRun(res.data.next_run || null);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.put(
        `${API}/admin/email-scheduler/heartbeat-schedule`,
        { hour: Number(hour), minute: Number(minute), timezone: tz },
        { withCredentials: true }
      );
      setNextRun(res.data.next_run || null);
      toast.success('Heartbeat rescheduled');
    } catch (e) {
      console.error(e);
      toast.error(e.response?.data?.detail || 'Failed to reschedule');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="heartbeat-schedule-editor"><Loader2 size={14} className="spinning" /> Loading schedule...</div>;
  }

  return (
    <div className="heartbeat-schedule-editor" data-testid="heartbeat-schedule-editor">
      <div className="hb-editor-row">
        <label className="hb-editor-label">Time:</label>
        <Input
          type="number"
          min="0"
          max="23"
          value={hour}
          onChange={(e) => setHour(e.target.value === '' ? 0 : Math.max(0, Math.min(23, parseInt(e.target.value, 10) || 0)))}
          className="hb-time-input"
          data-testid="hb-hour"
        />
        <span className="hb-colon">:</span>
        <Input
          type="number"
          min="0"
          max="59"
          value={minute}
          onChange={(e) => setMinute(e.target.value === '' ? 0 : Math.max(0, Math.min(59, parseInt(e.target.value, 10) || 0)))}
          className="hb-time-input"
          data-testid="hb-minute"
        />
        <select
          value={tz}
          onChange={(e) => setTz(e.target.value)}
          className="hb-tz-select"
          data-testid="hb-tz"
        >
          {COMMON_TIMEZONES.map(t => <option key={t} value={t}>{t}</option>)}
          {!COMMON_TIMEZONES.includes(tz) && <option value={tz}>{tz}</option>}
        </select>
        <Button
          onClick={save}
          disabled={saving}
          className="hb-save-btn"
          data-testid="hb-save"
        >
          {saving ? <><Loader2 size={14} className="spinning" /> Saving...</> : <><Save size={14} /> Save</>}
        </Button>
      </div>
      {nextRun && (
        <div className="hb-next-run" data-testid="hb-next-run">
          Next heartbeat: <strong>{new Date(nextRun).toLocaleString()}</strong>
        </div>
      )}
    </div>
  );
};

const SchedulerJobCard = ({
  jobKey, schedulerJobId, icon: Icon, title, description, scheduleText, prefField, extraSlot,
}) => {
  const ctx = useAdminEmail();
  const {
    schedulerStatus,
    loadScheduledRecipients, loadingScheduledRecipients,
    loadScheduledPreview, loadingScheduledPreview,
    sendScheduledTest, sendingScheduledTest,
    triggerEmailJob, triggeringJob,
  } = ctx;

  const nextRun = schedulerStatus?.jobs?.find(j => j.id === schedulerJobId)?.next_run;

  return (
    <div className="job-card">
      <div className="job-info">
        <div className="job-header">
          <Icon size={24} />
          <div>
            <h3>{title}</h3>
            <p>{description}</p>
          </div>
        </div>
        <div className="job-details">
          <div className="job-schedule">
            <Clock size={16} />
            <span>{scheduleText}</span>
          </div>
          {prefField && (
            <div className="job-pref-field" title="User preference field that controls who receives this email">
              <span className="pref-label">Filters on:</span>
              <code>email_preferences.{prefField}</code>
            </div>
          )}
          {nextRun && (
            <div className="job-next-run">
              Next run: {new Date(nextRun).toLocaleString()}
            </div>
          )}
          {extraSlot}
        </div>
      </div>
      <div className="job-actions">
        <Button
          onClick={() => loadScheduledRecipients(jobKey)}
          disabled={loadingScheduledRecipients === jobKey}
          className="action-btn secondary"
          title="View Recipients"
        >
          {loadingScheduledRecipients === jobKey ? <Loader2 size={16} className="spinning" /> : <List size={16} />} Recipients
        </Button>
        <Button
          onClick={() => loadScheduledPreview(jobKey)}
          disabled={loadingScheduledPreview === jobKey}
          className="action-btn secondary"
          title="Preview Email"
        >
          {loadingScheduledPreview === jobKey ? <Loader2 size={16} className="spinning" /> : <Eye size={16} />} Preview
        </Button>
        <Button
          onClick={() => sendScheduledTest(jobKey)}
          disabled={sendingScheduledTest === jobKey}
          className="action-btn secondary"
          title="Send Test Email"
        >
          {sendingScheduledTest === jobKey ? <Loader2 size={16} className="spinning" /> : <Send size={16} />} Test
        </Button>
        <Button
          onClick={() => triggerEmailJob(jobKey)}
          disabled={triggeringJob === jobKey}
          className="trigger-btn"
        >
          {triggeringJob === jobKey ? (
            <><Loader2 size={16} className="spinning" /> Running...</>
          ) : (
            <><Play size={16} /> Run Now</>
          )}
        </Button>
      </div>
    </div>
  );
};

const SchedulerTab = () => {
  const ctx = useAdminEmail();
  const {
    schedulerStatus,
    scheduledPreview, setScheduledPreview,
  } = ctx;

  return (
    <div className="scheduler-tab">
      <Card className="scheduler-card">
        <CardHeader>
          <CardTitle>Automated Email Jobs</CardTitle>
          <CardDescription>
            Scheduled emails that run automatically.{' '}
            Status: <span className={`status-badge ${schedulerStatus?.status || 'unknown'}`}>
              {schedulerStatus?.status || 'Loading...'}
            </span>
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="scheduler-jobs">
            <SchedulerJobCard
              jobKey="coming_episodes"
              schedulerJobId="coming_episodes_digest"
              icon={Calendar}
              title="Coming Episodes Digest"
              description="Weekly email with upcoming episodes for each user's shows"
              scheduleText="Every Saturday at 12:00 PM (NZ time)"
              prefField="weekly_digest"
            />
            <SchedulerJobCard
              jobKey="social_activity"
              schedulerJobId="social_activity_digest"
              icon={Users}
              title="Social Activity Digest"
              description="Weekly summary of followers, comments, and recommendations"
              scheduleText="Every Sunday at 10:00 AM UTC"
              prefField="social_notifications"
            />
            <SchedulerJobCard
              jobKey="grazing_recommendations"
              schedulerJobId="grazing_recommendations"
              icon={TrendingUp}
              title="Grazing Recommendations"
              description="Monthly AI-powered show recommendations"
              scheduleText="1st of each month at 9:00 AM UTC"
              prefField="grazing_recommendations"
            />
            <SchedulerJobCard
              jobKey="onboarding"
              schedulerJobId="onboarding_emails"
              icon={UserPlus}
              title="Onboarding Emails"
              description="Welcome series sent based on days since signup"
              scheduleText="Daily at 7:00 AM UTC"
              prefField="feature_announcements"
            />
            <SchedulerJobCard
              jobKey="heartbeat"
              schedulerJobId="heartbeat"
              icon={Activity}
              title="System Heartbeat"
              description="Daily dead-man-switch status report sent to dev only"
              scheduleText="Time configurable below"
              prefField={null}
              extraSlot={<HeartbeatScheduleEditor />}
            />
          </div>

          <div className="scheduler-note">
            <p><strong>Note:</strong> "Run Now" will only send emails to users who are opted-in and have relevant data. Check the History tab to see results.</p>
          </div>

          {scheduledPreview && (
            <div className="modal-overlay" onClick={() => setScheduledPreview(null)}>
              <div className="modal-content preview-modal" onClick={e => e.stopPropagation()}>
                <div className="modal-header">
                  <h3>Preview: {scheduledPreview.subject}</h3>
                  <button className="close-btn" onClick={() => setScheduledPreview(null)}>×</button>
                </div>
                <div className="preview-content">
                  <iframe
                    srcDoc={scheduledPreview.html}
                    title="Email Preview"
                    style={{ width: '100%', height: '500px', border: 'none', background: '#121212' }}
                  />
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SchedulerTab;
